import { invalidateCapacityContexts } from "~/utils/capacityInvalidation"
import { toast } from "react-toastify"
import { api, type RouterOutputs } from "~/utils/api"
import { showErrorToast } from "~/utils/toaster"

export type ISupplyPlan = RouterOutputs["supplyPlan"]["getAll"][number]

export type IBuy = RouterOutputs["supplyPlan"]["getBuysForSupplyPlan"][number]

export type BuyStatus = "Draft" | "Finalized" | "Archived"

export function deriveBuyStatus(buy: Pick<IBuy, "archived" | "finalizedAt">): BuyStatus {
  if (buy.archived) return "Archived"
  if (buy.finalizedAt) return "Finalized"
  return "Draft"
}

/** Draft buys, earliest buy date first and undated last: the order "Add to buy" offers them in. */
export function draftBuysByDate(buys: IBuy[] | undefined): IBuy[] {
  const time = (buy: IBuy) => (buy.buyDate ? new Date(buy.buyDate).getTime() : Infinity)
  return (buys ?? [])
    .filter((buy) => deriveBuyStatus(buy) === "Draft")
    .sort((a, b) => time(a) - time(b))
}

export type SupplyPlanSettings = {
  purchaseInterval: number
  respectMoq: boolean
  // Percentage 0-N. When > 0 (and respectMoq is on), if a purchase-order group's natural
  // demand is below `minMoqThresholdPct % × group MOQ`, the group's MOQ-based suggestion is
  // dropped to 0 instead of being rounded up to MOQ. Edited directly as a percentage in the UI.
  minMoqThresholdPct: number
  // Warehouse IDs for which safety stock is completely ignored in suggested order calculations.
  noSafetyStockWarehouseIds: string[]
  countDraftOrdersAsIntent: boolean
}

export const DEFAULT_SUPPLY_PLAN_SETTINGS: SupplyPlanSettings = {
  purchaseInterval: 30,
  respectMoq: false,
  minMoqThresholdPct: 0,
  noSafetyStockWarehouseIds: [],
  countDraftOrdersAsIntent: false,
}

// Recommended setup for new plans only; parseSettings keeps the old defaults so saved plans don't flip.
export const NEW_SUPPLY_PLAN_SETTINGS: SupplyPlanSettings = {
  ...DEFAULT_SUPPLY_PLAN_SETTINGS,
  respectMoq: true,
  countDraftOrdersAsIntent: true,
}

export const serializeSettings = (settings: SupplyPlanSettings): string => JSON.stringify(settings)

export const parseSettings = (settings: string | null | undefined): SupplyPlanSettings => {
  if (!settings) return DEFAULT_SUPPLY_PLAN_SETTINGS
  try {
    return {
      ...DEFAULT_SUPPLY_PLAN_SETTINGS,
      ...(JSON.parse(settings) as Partial<SupplyPlanSettings>),
    }
  } catch {
    return DEFAULT_SUPPLY_PLAN_SETTINGS
  }
}

/** Every supply plan list: NOOS plans, fashion plans, and the command menu's plan and buy search */
export const invalidateSupplyPlanListReads = (utils: ReturnType<typeof api.useUtils>) =>
  Promise.all([
    utils.supplyPlan.getAll.invalidate(),
    utils.purchasePlan.getAll.invalidate(),
    utils.commandSearch.supplyPlans.invalidate(),
  ])

export const useCreateSupplyPlan = () => {
  const utils = api.useUtils()
  return api.supplyPlan.create.useMutation({
    onSuccess: async () => {
      await invalidateSupplyPlanListReads(utils)
      toast.success("NOOS supply plan created.")
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export const useUpdateSupplyPlan = () => {
  const utils = api.useUtils()
  return api.supplyPlan.update.useMutation({
    onSuccess: async () => {
      await invalidateSupplyPlanListReads(utils)
      toast.success("NOOS supply plan updated.")
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export const useDeleteSupplyPlan = () => {
  const utils = api.useUtils()
  return api.supplyPlan.delete.useMutation({
    onSuccess: async () => {
      await Promise.all([
        invalidateSupplyPlanListReads(utils),
        utils.supplyPlan.getBuysForSupplyPlan.invalidate(),
        utils.purchaseOrder.getAll.invalidate(),
      ])
      toast.success("NOOS supply plan deleted.")
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export const useDeleteBuy = (supplyPlanId: number) => {
  const utils = api.useUtils()
  return api.supplyPlan.deleteBuy.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.supplyPlan.getBuysForSupplyPlan.invalidate({ supplyPlanId }),
        invalidateSupplyPlanListReads(utils),
        utils.purchaseOrder.getAll.invalidate(),
        utils.purchaseOrder.get.invalidate(),
        utils.stockOutTimes.getClosingStockAndSuggestedOrders.invalidate(),
        invalidateCapacityContexts(utils),
      ])
      toast.success("Buy deleted.")
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

// No onSuccess here: callers own the invalidation + toast once the full create flow completes.
export const useCreateBuy = () => {
  return api.supplyPlan.createBuy.useMutation({
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export type IBuyDetail = RouterOutputs["supplyPlan"]["getBuy"]
export type IBuyItem = IBuyDetail["items"][number]

export const useGetBuy = (buyId: number | undefined) => {
  return api.supplyPlan.getBuy.useQuery(
    { buyId: buyId ?? 0 },
    { enabled: buyId !== undefined && buyId > 0 },
  )
}

export const useUpdateBuyQuantities = () => {
  const utils = api.useUtils()
  return api.supplyPlan.updateBuyQuantities.useMutation({
    onSuccess: async (_data, variables) => {
      await utils.supplyPlan.getBuy.invalidate({ buyId: variables.buyId })
      await utils.supplyPlan.getBuysForSupplyPlan.invalidate()
      await utils.capacity.getBuyCapacityContext.invalidate()
      await invalidateCapacityContexts(utils)
      await utils.stockOutTimes.getClosingStockAndSuggestedOrders.invalidate()
      await utils.purchaseOrder.get.invalidate()
      toast.success("Buy quantities saved.")
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export const useSaveBuyWithReassignment = () => {
  const utils = api.useUtils()
  return api.supplyPlan.saveBuyWithReassignment.useMutation({
    onSuccess: async (_data, variables) => {
      await Promise.all([
        utils.supplyPlan.getBuy.invalidate({ buyId: variables.buyId }),
        utils.supplyPlan.getBuysForSupplyPlan.invalidate(),
        utils.capacity.getBuyCapacityContext.invalidate(),
        invalidateCapacityContexts(utils),
        utils.purchaseOrder.getAll.invalidate(),
        utils.purchaseOrder.get.invalidate(),
        utils.stockOutTimes.getClosingStockAndSuggestedOrders.invalidate(),
      ])
      toast.success("Buy saved.")
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}
